import React, { useEffect, useRef, useState } from "react";
import { Form } from "antd";
import Chart, { EChartsOption } from "../../../components/Chart";
import { LineSeriesOption } from "echarts/charts";
import PerformanceForm from "./PerformanceForm";
import { DataSource } from "../../../types";

import {
  calcYAxis,
  findIntersectionPoint,
  createQP,
  createQH2Data,
  createQEFF2Data,
  createQNPSH2Data,
  powerFormula,
  precision,
  createK,
  createAuxiliaryData,
  createDiameterData,
  createSpeedData,
  createFrequencyData,
  convertEFF1ToEFF2,
  getNpshrByData,
} from "../../../utils";

import { renderLabel, genRenderItem } from "../../../libs/echar";
import { DataPoint } from "regression";
import { useTranslation } from "react-i18next";
import ChartLayout from "../../../components/ChartLayout";
import { useSearchParams } from "react-router-dom";
import useChartImage from "../../../hooks/useChartImage";
import { EChartsType } from "echarts/core";
import { DisplayUnits } from "../displayUnits/definitions";
import {
  buildPerformanceBaseDisplayData,
  buildPerformanceCanonicalFormValues,
  buildPerformanceDisplayModel,
  buildPerformanceFormDisplayValues
} from "../displayUnits/adapter";
import {
  buildIntegerAxisLabelFormatter,
  FLOW_AXIS_SCALE_DIVISOR,
  formatFlowAxisName,
  formatDisplayMetricNumber,
  formatPowerDisplayValue,
  formatTooltipAxisHeader,
  formatTooltipSeriesValue,
  shouldUseFlowAxisHundreds,
} from "../displayUnits/displayFormatters";
import {
  convertDiameterFromCanonical,
  convertNpshrFromCanonical,
  convertPowerFromCanonical,
  formatAxisLabel,
  getDisplayUnitLabel
} from "../displayUnits/converters";
import {
  buildBasePowerDisplayCurve,
  buildExtendedPowerDisplayCurve
} from "../viscosity/powerCurve";
import { solveViscousAnchoredCurve } from "../viscosity/corrections";
import { VISCOSITY_QUERY_KEYS } from "../viscosity/converters";
import { buildLeadingDashedSeries } from "../leadingSegments";
import PerformanceInfoOverlay, {
  PerformanceInfoOverlayItem
} from "./PerformanceInfoOverlay";
import {
  downloadPerformanceChartImage,
  exportPerformanceChartImage
} from "./exportPerformanceChartImage";

// may be 多的 性能图表

const parseNumberParam = (input: string | null, isfloat: boolean = true) => {
  if (input === null) return undefined;
  const result = isfloat ? Number(input) : parseInt(input);
  if (!result) return undefined;

  return result;
};

interface PerformanceChartProps {
  dataSource: DataSource;
  displayUnits?: DisplayUnits;
  setParamsWithDisplayUnits?: (values: Record<string, string | number | undefined>) => void;
}

const PerformanceChart = ({
  dataSource,
  displayUnits,
  setParamsWithDisplayUnits
}: PerformanceChartProps) => {
  const finalDisplayUnits: DisplayUnits = displayUnits || {
    flow: "m3/h",
    head: "m",
    power: "kW",
    npshr: "m",
    diameter: "mm",
    viscosity: "cSt",
    efficiency: "%"
  };
    const displayDataSource = buildPerformanceBaseDisplayData(dataSource, finalDisplayUnits);
    const displayBasePowerData = buildBasePowerDisplayCurve(displayDataSource.QP);
    const flowAxisMax = Math.max(
      0,
      ...displayDataSource.QH.map((point) => point[0] || 0),
      ...displayDataSource.QP.map((point) => point[0] || 0),
      ...displayDataSource.QEFF.map((point) => point[0] || 0),
      ...displayDataSource.QNPSH.map((point) => point[0] || 0)
    );
    const useScaledFlowAxis = shouldUseFlowAxisHundreds(flowAxisMax);
    const flowAxisLabelFormatter = buildIntegerAxisLabelFormatter("flow", {
      scaleDivisor: useScaledFlowAxis ? FLOW_AXIS_SCALE_DIVISOR : undefined,
    });
    const flowAxisName = formatFlowAxisName(
      formatAxisLabel("flow", finalDisplayUnits.flow),
      flowAxisMax
    );
  const defaultSeries: LineSeriesOption[] = [
    {
      name: "QH1",
      type: "line",
      label: renderLabel("Q-H"),
      smooth: true,
      symbolSize: 0,
      lineStyle: {
        width: 1,
      },
      data: displayDataSource.QH,
      color: "black",
      animation: false,
    },
    {
      name: "QEFF",
      type: "line",
      label: renderLabel("Q-EFF"),
      smooth: true,
      symbolSize: 0,
      lineStyle: {
        width: 1,
      },
      data: displayDataSource.QEFF,
      color: "black",
      xAxisIndex: 0,
      yAxisIndex: 1,
      animation: false,
    },
    {
      name: "QP1",
      type: "line",
      label: renderLabel("Q-P"),
      smooth: false,
      symbolSize: 0,
      lineStyle: {
        width: 1,
      },
      data: displayBasePowerData,
      color: "black",
      xAxisIndex: 1,
      yAxisIndex: 2,
      animation: false,
    },
    {
      name: "QNPSH",
      type: "line",
      label: renderLabel("Q-NPSHr"),
      smooth: true,
      symbolSize: 0,
      lineStyle: {
        width: 1,
      },
      data: displayDataSource.QNPSH,
      color: "black",
      xAxisIndex: 1,
      yAxisIndex: 3,
      animation: false,
    },
  ];

  const initOption: EChartsOption = {
    tooltip: {
      trigger: "axis",
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params];
        const axisValue = items[0]?.axisValue;
        const lines = [formatTooltipAxisHeader(axisValue)];

        items.forEach((item) => {
          if (!item?.seriesName || item.seriesType === "custom") {
            return;
          }

          lines.push(
            `${item.marker}${item.seriesName}&nbsp;&nbsp;<b>${formatTooltipSeriesValue(
              item.seriesName,
              item.value
            )}</b>`
          );
        });

        return lines.join("<br/>");
      },
      axisPointer: {
        type: "cross",
      },
    },
    toolbox: {
      feature: {
        mySaveAsImage: {
          name: dataSource.name,
          title: "Save as image",
          icon: "path://M18 20H6v-2h12v2ZM12 16l-5-5h3V4h4v7h3l-5 5Z",
          onclick: () => handleSaveChartImage(),
          pixelRatio: 2,
        },
      },
    },
    grid: [{ bottom: "40%" }, { top: "70%" }],
    xAxis: [
      // Q1
      {
        name: formatAxisLabel("flow", finalDisplayUnits.flow),
        type: "value",
        nameGap: 18,
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("flow"),
        },
        axisPointer: {
          label: {
            formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
          },
        },
      },
      // Q2
      {
        name: formatAxisLabel("flow", finalDisplayUnits.flow),
        type: "value",
        gridIndex: 1,
        nameGap: 18,
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("flow"),
        },
        axisPointer: {
          label: {
            formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
          },
        },
      },
    ],
    yAxis: [
      // H
      {
        name: formatAxisLabel("head", finalDisplayUnits.head),
        type: "value",
        min: 0,
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("head"),
        },
        axisPointer: {
          label: {
            formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("head")(value, 0),
          },
        },
      },
      // EFF
      {
        name: formatAxisLabel("efficiency", finalDisplayUnits.efficiency),
        type: "value",
        min: 0,
        max: 200,
        splitNumber: 10,
        axisPointer: {
          label: {
            show: false,
          },
        },
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("efficiency", {
            shouldDisplay: (value) => value <= 100,
          }),
        }
      },
      // P
      {
        name: formatAxisLabel("power", finalDisplayUnits.power),
        type: "value",
        min: 0,
        gridIndex: 1,
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("power"),
        },
        axisPointer: {
          label: {
            formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("power")(value, 0),
          },
        },
      },
      // NPSHr
      {
        name: formatAxisLabel("npshr", finalDisplayUnits.npshr),
        type: "value",
        gridIndex: 1,
        min: 0,
        max: 15,
        splitNumber: 5,
        axisPointer: {
          label: {
            show: false,
          },
        },
      },
    ],
    series: defaultSeries,
  };

  const [option, setOption] = useState(initOption);
  const [overlayTopItems, setOverlayTopItems] = useState<PerformanceInfoOverlayItem[]>([]);
  const [overlayBottomItems, setOverlayBottomItems] = useState<PerformanceInfoOverlayItem[]>([]);
  const chartInstanceRef = useRef<EChartsType | null>(null);
  const overlayTopItemsRef = useRef<PerformanceInfoOverlayItem[]>([]);
  const overlayBottomItemsRef = useRef<PerformanceInfoOverlayItem[]>([]);
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();

  const quantity = parseNumberParam(searchParams.get("q"));
  const head = parseNumberParam(searchParams.get("h"));
  const type = parseNumberParam(searchParams.get("type"), false) || 0;
  const sg = parseNumberParam(searchParams.get("sg")) || 1;
  const viscosityCanonical = parseNumberParam(searchParams.get(VISCOSITY_QUERY_KEYS.value));

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataSource,
    quantity,
    head,
    type,
    sg,
    viscosityCanonical,
    finalDisplayUnits.flow,
    finalDisplayUnits.head,
    finalDisplayUnits.power,
    finalDisplayUnits.npshr,
    finalDisplayUnits.diameter,
    finalDisplayUnits.viscosity,
    i18n.language
  ]);

  const handleSubmit = (values: any): void => {
    const { quantity, head, type, sg, viscosity } = values;
    const canonicalValues = buildPerformanceCanonicalFormValues(
      { quantity, head, sg, viscosity },
      finalDisplayUnits
    );

    if (canonicalValues.quantity && canonicalValues.head) {
      if (setParamsWithDisplayUnits) {
        setParamsWithDisplayUnits({
          q: canonicalValues.quantity,
          h: canonicalValues.head,
          type,
          sg: sg || 1,
          [VISCOSITY_QUERY_KEYS.value]: canonicalValues.viscosity
        });
      } else {
        const nextParams: any = {
          q: `${canonicalValues.quantity}`,
          h: `${canonicalValues.head}`,
          type,
          sg: sg || 1
        };

        if (canonicalValues.viscosity !== undefined) {
          nextParams[VISCOSITY_QUERY_KEYS.value] = canonicalValues.viscosity;
        }

        setSearchParams(nextParams);
      }
    }
  };

  const { addImage, delImage, exportTemplate } = useChartImage();

  useEffect(() => {
    overlayTopItemsRef.current = overlayTopItems;
    overlayBottomItemsRef.current = overlayBottomItems;
  }, [overlayTopItems, overlayBottomItems]);

  const handleSaveChartImage = () => {
    if (!chartInstanceRef.current) {
      return;
    }

    downloadPerformanceChartImage(
      chartInstanceRef.current,
      dataSource.name,
      overlayTopItemsRef.current,
      overlayBottomItemsRef.current
    );
  };

  const handleReset = (): void => {
    form.resetFields();
    delImage("performanceChart");
    if (setParamsWithDisplayUnits) {
      setParamsWithDisplayUnits({});
    } else {
      setSearchParams({});
    }
  };

  const handleChartChange = (instance: EChartsType) => {
    chartInstanceRef.current = instance;
    const result = exportPerformanceChartImage(
      instance,
      exportTemplate as any,
      overlayTopItems,
      overlayBottomItems
    );
    addImage("performanceChart", result);
  };

  const draw = (): void => {
    setOverlayTopItems([]);
    setOverlayBottomItems([]);
    // if don't have data, return (this never happend before)
    if (!dataSource) return;
    // get the interval of P and H
    const [hMax, hInterval] = calcYAxis(
      displayDataSource.QH.map((p) => p[1]),
      10,
      1
    );
    const [pMax, pInterval] = calcYAxis(
      displayBasePowerData.map((p) => p[1]),
      5,
      1
    );
    // create the origin chart
    let option: EChartsOption = {
      ...initOption,
      xAxis: [
        {
          name: flowAxisName,
          type: "value",
          nameGap: 18,
          axisLabel: {
            formatter: flowAxisLabelFormatter,
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => flowAxisLabelFormatter(value, 0),
            },
          },
        },
        {
          name: flowAxisName,
          type: "value",
          gridIndex: 1,
          nameGap: 18,
          axisLabel: {
            formatter: flowAxisLabelFormatter,
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => flowAxisLabelFormatter(value, 0),
            },
          },
        },
      ],
      yAxis: [
        // H
        {
          name: formatAxisLabel("head", finalDisplayUnits.head),
          type: "value",
          min: 0,
          max: hMax,
          interval: hInterval,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("head"),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("head")(value, 0),
            },
          },
        },
        // EFF
        {
          name: formatAxisLabel("efficiency", finalDisplayUnits.efficiency),
          type: "value",
          min: 0,
          max: 200,
          splitNumber: 10,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("efficiency", {
              shouldDisplay: (value) => value <= 100,
            }),
          },
          axisPointer: {
            label: {
              show: false,
            },
          }
        },
        // P
        {
          name: formatAxisLabel("power", finalDisplayUnits.power),
          type: "value",
          min: 0,
          max: pMax,
          interval: pInterval,
          gridIndex: 1,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("power"),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("power")(value, 0),
            },
          },
        },
        // NPSH
        {
          name: formatAxisLabel("npshr", finalDisplayUnits.npshr),
          type: "value",
          gridIndex: 1,
          min: 0,
          max: 15,
          splitNumber: 5,
          axisPointer: {
            label: {
              show: false,
            },
          },
        },
      ],
    };
    // if it doesn't have params or params are invalid, return
    if (quantity === undefined || head === undefined) {
      setOption(option);
      form.resetFields();
      form.setFieldsValue({
        quantityUnit: getDisplayUnitLabel("flow", finalDisplayUnits.flow),
        headUnit: getDisplayUnitLabel("head", finalDisplayUnits.head),
        diameterUnit: getDisplayUnitLabel("diameter", finalDisplayUnits.diameter),
        powerUnit: getDisplayUnitLabel("power", finalDisplayUnits.power),
        npshUnit: getDisplayUnitLabel("npshr", finalDisplayUnits.npshr),
        viscosityUnit: getDisplayUnitLabel("viscosity", finalDisplayUnits.viscosity),
      });
      return;
    }
    // if it has params and params are valid, draw other lines
    //  - calculate the intersection point
    const center = (dataSource.range.start + dataSource.range.end) / 2;
    const intersectionPoint = findIntersectionPoint(
      quantity,
      head,
      dataSource.QH.equation,
      type,
      center
    );
    if (!intersectionPoint) return;
    //  - create lines
    // 生成辅助线数据
    const auxData = createAuxiliaryData(
      quantity,
      head,
      0,
      intersectionPoint[0],
      type
    );

    // 根据辅助线生成新的 Q-H 线
    const k: number = createK(quantity, intersectionPoint[0], type);
    const qH2Data = createQH2Data(k, dataSource.QH.dummyPoints, type);

    // 平移 Q-EFF
    let qEFF2Data = createQEFF2Data(k, dataSource.QEFF.dummyPoints, type);

    // 平移 Q-NPSH
    let qNPSH2Data: any;
    if (dataSource.QNPSH) {
      qNPSH2Data = createQNPSH2Data(k, dataSource.QNPSH.dummyPoints, type);
    }

    // 平移 Q-P
    const qh2Points = createQH2Data(
      k,
      dataSource.QH.points as DataPoint[],
      type
    );

    const qEFF2Points = createQEFF2Data(
      k,
      dataSource.QEFF.points as DataPoint[],
      type
    );

    const qP2Data = createQP(qh2Points, qEFF2Points, sg);

    const solvedCurve = solveViscousAnchoredCurve({
      quantity,
      head,
      sg,
      viscosityCanonical,
      speed: dataSource.speed,
      type,
      qhPoints: dataSource.QH.dummyPoints,
      qeffPoints: dataSource.QEFF.dummyPoints,
      qnpshPoints: dataSource.QNPSH ? dataSource.QNPSH.dummyPoints : [],
      qhRawPoints: dataSource.QH.points as DataPoint[],
      qeffRawPoints: dataSource.QEFF.points as DataPoint[],
      qhEquation: dataSource.QH.equation,
      center
    });
    const resolvedK = solvedCurve?.k ?? k;

    // 叶轮
    const { diameter, diameterUnit } = createDiameterData(
      resolvedK,
      dataSource.diameter,
      type
    );

    // 转速
    const speed = createSpeedData(resolvedK, dataSource.speed, type);

    // 频率
    const frequency = createFrequencyData(resolvedK, dataSource.frequency, type);

    const finalQH2Data = solvedCurve?.viscousCurves.qH2v || qH2Data;
    const finalQEFF2Data = solvedCurve?.viscousCurves.qEFF2v || qEFF2Data;
    const finalQNPSH2Data = solvedCurve?.viscousCurves.qNPSH2v || qNPSH2Data || [];

    // EFF
    let eff = solvedCurve?.correctedDutyPointResult.eff ?? convertEFF1ToEFF2(dataSource.QEFF, intersectionPoint[0], k);

    // P
    let power = solvedCurve?.correctedDutyPointResult.power ?? (powerFormula([quantity, head], eff / 100) * sg);

    const finalQP2Data = buildExtendedPowerDisplayCurve(
      displayDataSource.QP,
      solvedCurve?.viscousCurves.qP2vTrusted?.length ? solvedCurve.viscousCurves.qP2vTrusted : qP2Data,
      {
        anchorPoint: [quantity, power]
      }
    );

    // NPSH
    let npsh: number | null = solvedCurve?.correctedDutyPointResult.npsh ?? null;
    if (npsh === null && dataSource.QNPSH) {
      npsh = getNpshrByData(qNPSH2Data, quantity); // catmullRom
    }

    const displayModel = buildPerformanceDisplayModel({
      displayUnits: finalDisplayUnits,
      quantity,
      head,
      power,
      npsh,
      diameter,
      diameterUnit,
      auxData,
      qH2Data: finalQH2Data,
      qEFF2Data: finalQEFF2Data,
      qNPSH2Data: finalQNPSH2Data,
      qP2Data: finalQP2Data
    });
    const displayFormValues = buildPerformanceFormDisplayValues(
      { quantity, head, viscosity: viscosityCanonical, sg },
      finalDisplayUnits
    );
    const displayPower = convertPowerFromCanonical(power, finalDisplayUnits.power) as number;
    const displayNpsh = convertNpshrFromCanonical(npsh, finalDisplayUnits.npshr) as number | null;
    const displayDiameter = diameterUnit === "%"
      ? diameter
      : (convertDiameterFromCanonical(diameter, finalDisplayUnits.diameter) as number);
    const displayDiameterUnit = diameterUnit === "%"
      ? "%"
      : getDisplayUnitLabel("diameter", finalDisplayUnits.diameter);

    //  - set form fields
    form.setFieldsValue({
      quantity: displayFormValues.quantity,
      head: displayFormValues.head,
      sg,
      viscosity: displayFormValues.viscosity,
      type,
      diameter: displayDiameter,
      speed,
      frequency,
      power: displayPower,
      npsh: displayNpsh,
      eff,
      quantityUnit: getDisplayUnitLabel("flow", finalDisplayUnits.flow),
      headUnit: getDisplayUnitLabel("head", finalDisplayUnits.head),
      diameterUnit: displayDiameterUnit,
      powerUnit: getDisplayUnitLabel("power", finalDisplayUnits.power),
      npshUnit: getDisplayUnitLabel("npshr", finalDisplayUnits.npshr),
      viscosityUnit: getDisplayUnitLabel("viscosity", finalDisplayUnits.viscosity),
    });

    setOption({
      ...option,
      series: [
        ...defaultSeries,
        {
          name: "aux",
          type: "line",
          smooth: true,
          symbolSize: 0,
          lineStyle: { width: 1 },
          color: "#aaa",
          data: displayModel.auxData,
          animation: false,
        },
        {
          name: "QH2",
          type: "line",
          smooth: true,
          symbolSize: 0,
          color: "#aaa",
          label: renderLabel(
            type === 0
              ? `${precision(speed, 0)} rpm`
              : `${precision(displayDiameter, 0)} ${displayDiameterUnit}`,
            [50, 35]
          ),
          data: displayModel.qH2Data,
          animation: false,
        },
        {
          name: "QEFF2",
          type: "line",
          smooth: true,
          symbolSize: 0,
          lineStyle: { width: 1 },
          xAxisIndex: 0,
          yAxisIndex: 1,
          color: "#aaa",
          data: displayModel.qEFF2Data,
          animation: false,
        },
        {
          name: "QNPSH2",
          type: "line",
          smooth: true,
          symbolSize: 0,
          lineStyle: { width: 1 },
          xAxisIndex: 1,
          yAxisIndex: 3,
          color: "#aaa",
          data: displayModel.qNPSH2Data,
          animation: false,
        },
        ...buildLeadingDashedSeries({
          name: "QP2",
          type: "line",
          smooth: false,
          symbolSize: 0,
          lineStyle: { width: 1 },
          xAxisIndex: 1,
          yAxisIndex: 2,
          color: "#aaa",
          animation: false,
        }, displayModel.qP2Data),
        {
          name: "intersection point",
          type: "custom",
          renderItem: genRenderItem("circle", displayModel.quantity, displayModel.head),
          data: [
            {
              name: "intersection point",
              xAxis: displayModel.quantity,
              yAxis: displayModel.head,
            },
          ],
          z: 100,
        },
      ],
    });

    setOverlayTopItems([
      { label: "Q", value: formatDisplayMetricNumber("flow", displayModel.quantity), unit: displayModel.units.Q },
      { label: "H", value: formatDisplayMetricNumber("head", displayModel.head), unit: displayModel.units.H },
      {
        label: t("chart.diameter"),
        value: precision(displayDiameter, 0),
        unit: displayDiameterUnit,
      },
      {
        label: t("chart.speed"),
        value: precision(speed, 0),
        unit: "rpm",
      },
      {
        label: t("chart.efficiency"),
        value: formatDisplayMetricNumber("efficiency", eff),
        unit: "%",
      },
      {
        label: t("chart.sg"),
        value: precision(sg, 2),
      },
    ]);

    setOverlayBottomItems([
      { label: "P", value: formatPowerDisplayValue(displayPower), unit: displayModel.units.P },
      {
        label: "NPSHr",
        value:
          displayNpsh === null || displayNpsh === undefined
            ? displayNpsh
            : formatDisplayMetricNumber("npshr", displayNpsh),
        unit: displayModel.units.NPSH,
      },
    ]);
  };

  return (
    <ChartLayout
      chart={
        <div className="performance-chart-main" style={{ position: "relative", width: 500, height: 700, margin: "auto" }}>
          <Chart
            option={option}
            notMerge
            style={{
              width: "500px",
              height: "700px",
              margin: "auto",
            }}
            onChange={handleChartChange}
          />
          <PerformanceInfoOverlay
            topItems={overlayTopItems}
            bottomItems={overlayBottomItems}
          />
        </div>
      }
      form={
        <PerformanceForm
          onFinish={handleSubmit}
          form={form}
          onReset={handleReset}
          type={dataSource.type}
          displayUnits={finalDisplayUnits}
        />
      }
    />
  );
};

export default PerformanceChart;
